// AbsoluteSlider — the shared slider in *absolute mode* (#238).
//
// The sibling of DeltaSlider: same native <input type="range">, thumb, focus
// ring, palette, and Shiny bridge — but the track fill emerges from the LEFT
// (min → value) rather than from the default, because here the value itself is
// the message ("set the VAT rate to 21%"), not the change. A baseline marker
// sits on the track at the default (the "modified-from-default visibly marked"
// UX principle), and sparse end labels show the bounds. Replaces the bslib
// ionRangeSlider for every numeric/rate group (VAT, PIT zero-rate band, IIT).
//
// Like the other islands it writes a single numeric value to Shiny via
// setInputValue(inputId, …), so the server-side panel reactive at
// input[[param$id]] reads exactly the scalar the bslib slider produced — the
// engine boundary is unchanged.

import { useCallback, useEffect, useMemo, useState } from "react";
import type { AbsoluteSliderProps } from "./types";
import { useResetAllSignal } from "../../state/reset_signal";
import { recordControl } from "../../state/share_store";
import type { ControlMeta } from "../../state/share_store";
import { notifyRestored, restoredNumber } from "../../state/share_restore";
import {
  absoluteFillBackground,
  baselinePercent,
  formatAbsolute,
} from "./absolute_slider_logic";
import { InfoIcon } from "../info_icon";

export function AbsoluteSlider({
  inputId,
  label,
  instrument,
  description,
  defaultValue,
  min,
  max,
  step,
  format,
  onValueChange,
}: AbsoluteSliderProps): JSX.Element {
  const [value, setValue] = useState<number>(() =>
    restoredNumber(inputId, defaultValue, min, max),
  );
  const reset = useCallback(() => setValue(defaultValue), [defaultValue]);
  // Page-level "Reset all" reaches React islands via the document-delegated
  // click signal (this slider isn't a Shiny widget, so the server reset can't
  // reach it). #132.
  useResetAllSignal(reset);

  // Static display metadata for the "Your reform" modal (#65), registered
  // alongside the value so the modal can show this control's label, level, and
  // delta as a pure store selector.
  const meta = useMemo<ControlMeta>(
    () => ({ kind: "numeric", label, instrument, default: defaultValue, format }),
    [label, instrument, defaultValue, format],
  );

  // Push the current value to the Shiny side on every change. "deferred"
  // debounces by Shiny's natural flush boundaries; the server reads the latest
  // value when the user clicks Simulate.
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.Shiny?.setInputValue?.(inputId, value, { priority: "deferred" });
    // Mirror into the share registry for the Copy-link button (#244) and the
    // reform modal (#65).
    recordControl(inputId, value, value !== defaultValue, meta);
    // Report a restored control so the shared reform auto-runs once every
    // control has committed (#297). No-op unless this id was restored.
    notifyRestored(inputId);
  }, [inputId, value, defaultValue, meta]);

  // Mirror the value to an optional host. Kept separate from the Shiny push so
  // a host that doesn't pass it costs nothing.
  useEffect(() => {
    onValueChange?.(value);
  }, [onValueChange, value]);

  const sliderStyle = useMemo<React.CSSProperties>(
    () => ({ background: absoluteFillBackground(min, max, value) }),
    [value, min, max],
  );
  const baselineStyle = useMemo<React.CSSProperties>(
    () => ({ left: `${baselinePercent(min, max, defaultValue)}%` }),
    [min, max, defaultValue],
  );

  const sliderId = `${inputId}--slider`;
  const isModified = value !== defaultValue;
  const readout = formatAbsolute(value, format);

  return (
    <div
      className="beamm-absolute-slider"
      data-testid={`absolute-slider-${inputId}`}
    >
      {/* Header: label (left) + value and reset (right). Keeping the value off
          the track row lets the track span the full width, so the min/max end
          labels line up under the track ends and read as part of the slider. */}
      <div className="beamm-absolute-slider__header">
        <label
          htmlFor={sliderId}
          className="beamm-absolute-slider__label beamm-input-island__label"
        >
          <span>{label}</span>
          {description ? <InfoIcon description={description} /> : null}
        </label>
        <span className="beamm-absolute-slider__value-group">
          <span
            className="beamm-absolute-slider__value"
            data-testid={`absolute-slider-value-${inputId}`}
          >
            {readout}
          </span>
          {isModified ? (
            <button
              type="button"
              className="beamm-absolute-slider__reset beamm-input-island__reset"
              data-testid={`absolute-slider-reset-${inputId}`}
              onClick={reset}
            >
              Reset
            </button>
          ) : null}
        </span>
      </div>
      <div className="beamm-absolute-slider__track">
        <input
          id={sliderId}
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => setValue(Number(e.target.value))}
          className="beamm-absolute-slider__input"
          data-testid={`absolute-slider-input-${inputId}`}
          aria-label={label}
          aria-valuetext={readout}
          style={sliderStyle}
        />
        {/* Baseline marker at the default — decorative, so hidden from
            assistive tech (the default is conveyed by aria-valuetext and the
            reset affordance). */}
        <span
          className="beamm-absolute-slider__baseline"
          data-testid={`absolute-slider-baseline-${inputId}`}
          style={baselineStyle}
          aria-hidden="true"
        />
      </div>
      {/* Sparse end labels under the track ends — the bounds, replacing the
          dense bslib tick row. */}
      <div className="beamm-absolute-slider__ends" aria-hidden="true">
        <span>{formatAbsolute(min, format)}</span>
        <span>{formatAbsolute(max, format)}</span>
      </div>
    </div>
  );
}
